import { Fragment, cloneElement, useMemo } from 'react';
import defaults from 'lodash/defaults';
import forEachRight from 'lodash/forEachRight';

// specific for Facebook since FB API gave back fucked offset :stare
const getChars = (str) => (Array.from ? Array.from(str) : arrayFrom(str));

const arrayFrom = (str) => toCodePoints(str)?.map((codepoint) => String?.fromCodePoint(codepoint));

// needed to take into account that emoji length defer between emojis
const toCodePoints = (str) => {
    const chars = [];
    for (let i = 0; i < str.length; i++) {
        const c1 = str.charCodeAt(i);
        if (c1 >= 0xd800 && c1 < 0xdc00 && i + 1 < str.length) {
            let c2 = str.charCodeAt(i + 1);
            if (c2 >= 0xdc00 && c2 < 0xe000) {
                chars.push(0x10000 + ((c1 - 0xd800) << 10) + (c2 - 0xdc00));
                i++;
                continue;
            }
        }
        chars.push(c1);
    }
    return chars;
};

const addTag = (text, tag, element) => {
    const { name, offset, length } = tag;
    const htmlEl = `<${element}>${name}</${element}>`;

    const chars = getChars(text);
    return [...chars.slice(0, offset), htmlEl, ...chars.slice(offset + length)].join('');
};

// Needed for custom OffsetReplace mimic next-translate <Trans /> component
const tagRe = /<(\w+)>(.*?)<\/\1>|<(\w+)\/>/;
const nlRe = /(?:\r\n|\r|\n)/g;

function getElements(parts) {
    if (!parts?.length) return [];

    const [paired, children, unpaired, after] = parts.slice(0, 4);

    return [[paired || unpaired, children || '', after]]?.concat(
        getElements(parts?.slice(4, parts?.length))
    );
}

function formatElements(value, elements) {
    const parts = value?.replace(nlRe, '')?.split(tagRe);

    if (parts?.length === 1) return value;

    const tree = [];

    const before = parts?.shift();
    if (before) tree?.push(before);

    getElements(parts)?.forEach(([key, children, after], realIndex) => {
        const element = elements[key] || <Fragment />;

        tree?.push(
            cloneElement(
                element,
                { key: realIndex },

                // format children for pair tags
                // unpaired tags might have children if it's a component passed as a variable
                children ? formatElements(children, elements) : element.props.children
            )
        );

        if (after) tree?.push(after);
    });
    return tree;
}

export const useTransformStringOnOffset = () => {
    // loop throw text and insert html tag based on offset
    const formatTextWithComponent = (text, offsetList, htmlTag) => {
        let textWithLinks = text;
        if (offsetList?.length > 0) {
            let addedOffset = 0;
            offsetList.forEach((offset, id) => {
                const adaptedOffset = offset.offset + addedOffset;
                const beforeTag = textWithLinks.substring(0, adaptedOffset);
                const afterTag = textWithLinks.substring(
                    adaptedOffset + offset.length,
                    textWithLinks.length
                );
                const component = `<${htmlTag ?? id}>${offset.name}</${htmlTag ?? id}>`;
                const addedLength = component.length - offset.name.length;
                addedOffset += addedLength;
                textWithLinks = beforeTag + component + afterTag;
            });
        }

        return textWithLinks
            ?.split('\n')
            ?.map((paragraph) => (paragraph?.length ? `<p>${paragraph}</p><br/>` : '<br/>'))
            ?.join('')
            ?.replace(/\n/g, '<br/>');
    };

    // Needed since FB api return wrong offset
    const formatFacebookText = (message, tags = [], htmlEl, options) => {
        const opts = defaults({}, options, {
            addTags: true,
            addParagraphs: true
        });

        if (opts.addTags) {
            forEachRight(tags, (tag, id) => {
                message = htmlEl ? addTag(message, tag, htmlEl) : addTag(message, tag, id);
            });
        }

        if (opts.addParagraphs) {
            message = message
                ?.split('\n')
                ?.map((paragraph) => (paragraph?.length ? `<p>${paragraph}</p><br/>` : '<br/>'))
                ?.join('')
                ?.replace(/\n/g, '<br/>');
        }

        return message;
    };

    // usage exemple
    {
        /* 
            data.tags exemple structure
            [{offset: 230, length: 7, name: "#tweets", link: "https://www.facebook.com/497375430321149"}]

            // if each element have the same tag without specific needs you con pass the tag as third param
            <OffsetReplace
                text={formatTextWithComponent(data?.caption, data?.tags, 'span')}
                components={{ span: <span className="text-icoBlue" /> }}
            /> 

            // if you need to add specificity to each tag element

            <OffsetReplace
                text={formatTextWithComponent(data?.caption, data?.tags)}
                components={Object.assign(
                    {},
                    data?.tags?.map((tag, id) => (
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                            key={id}
                            href={tag.link}
                            className="text-icoBlue"
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    ))
                )}
            /> 
        */
    }
    function OffsetReplace({ text, components }) {
        /**
         * Memorize the transformation
         */
        const result = useMemo(() => {
            if (!components || components.length === 0) return text;

            return formatElements(text, components);
        }, [text, components]);

        return result;
    }
    return { formatTextWithComponent, OffsetReplace, formatFacebookText };
};
